import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiTruck, FiCreditCard, FiArrowLeft, FiX } = FiIcons;

export default function CheckoutFlow({ isOpen, onClose, cart, onComplete }) {
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const handleComplete = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setStep(3);
      setTimeout(() => onComplete(), 5000);
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-[var(--navy)]/95 backdrop-blur-md" onClick={onClose} />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl"
      >
        <button onClick={onClose} className="absolute top-6 right-6 z-10 p-2 hover:bg-gray-100 rounded-full text-[var(--navy)]"><SafeIcon icon={FiX} /></button>

        <div className="flex flex-col h-full">
          {/* Progress Header */}
          <div className="bg-[var(--kraft-light)] p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-[var(--navy)] text-[var(--mint)]' : 'bg-gray-200'}`}><SafeIcon icon={FiTruck} /></div>
              <div className="h-[2px] flex-grow bg-gray-200" />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-[var(--navy)] text-[var(--mint)]' : 'bg-gray-200'}`}><SafeIcon icon={FiCreditCard} /></div>
              <div className="h-[2px] flex-grow bg-gray-200" />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= 3 ? 'bg-[var(--navy)] text-[var(--mint)]' : 'bg-gray-200'}`}><SafeIcon icon={FiCheckCircle} /></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--navy)] italic">
              {step === 1 && "Circular Logistics"}
              {step === 2 && "Secure Settlement"}
              {step === 3 && "Impact Initiated"}
            </h2>
          </div>

          <div className="p-8 md:p-12 flex-grow">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Full Name</label>
                      <input type="text" className="w-full p-4 bg-[var(--off-white)] border border-gray-100 rounded-sm focus:border-[var(--mint-dark)] outline-none" defaultValue="Julianne Miller" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Email</label>
                      <input type="email" className="w-full p-4 bg-[var(--off-white)] border border-gray-100 rounded-sm focus:border-[var(--mint-dark)] outline-none" defaultValue="julianne@example.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Shipping Address</label>
                    <input type="text" className="w-full p-4 bg-[var(--off-white)] border border-gray-100 rounded-sm focus:border-[var(--mint-dark)] outline-none" defaultValue="123 Circular Way, Portland, OR 97201" />
                  </div>
                  <button onClick={() => setStep(2)} className="w-full py-5 bg-[var(--navy)] text-[var(--mint)] font-bold rounded-sm mt-8">Continue to Payment</button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  <div className="p-6 bg-[var(--kraft-light)] rounded-lg flex justify-between items-center mb-8">
                    <span className="font-bold text-[var(--navy)]">Settlement Amount</span>
                    <span className="text-xl font-bold">${cart.reduce((a, b) => a + (b.price * b.quantity), 0).toFixed(2)}</span>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Card Number</label>
                    <div className="relative">
                      <input type="text" className="w-full p-4 bg-[var(--off-white)] border border-gray-100 rounded-sm outline-none" placeholder="•••• •••• •••• ••••" />
                      <SafeIcon icon={FiCreditCard} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <button onClick={() => setStep(1)} className="flex items-center gap-2 text-[var(--navy)] font-bold"><SafeIcon icon={FiArrowLeft} /> Back</button>
                    <button onClick={handleComplete} disabled={isProcessing} className="flex-grow py-5 bg-[var(--navy)] text-[var(--mint)] font-bold rounded-sm">
                      {isProcessing ? "Validating Platform..." : "Finalize Selection"}
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                  <div className="w-24 h-24 bg-[var(--mint)] text-[var(--navy)] rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl">
                    <SafeIcon icon={FiCheckCircle} className="text-5xl" />
                  </div>
                  <h3 className="text-3xl font-bold text-[var(--navy)] mb-4 italic">Thank You for Inhabiting the Loop.</h3>
                  <p className="text-gray-500 max-w-md mx-auto leading-relaxed mb-8">
                    Your circular kit is being assembled. You've just diverted approximately 4.2kg of single-use plastic from our oceans.
                  </p>
                  <button onClick={onClose} className="px-10 py-4 border-2 border-[var(--navy)] font-bold uppercase tracking-widest text-xs hover:bg-[var(--navy)] hover:text-white transition-all">Return to Platform</button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}